"use client";
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getStartDate, getEndDate } from "@/redux/search/searchSlice";
import dayjs from "dayjs";

// Must stay >= the API's rule: SearchController rejects a start under 30 minutes
// away ("Start date must be 30 minutes from now") and Cart auto-empties inside
// AUTO_CLEANUP_THRESHOLD_SECONDS (1800). Lowering this alone breaks search.
const MIN_LEAD_MINUTES = 30;
const SLOT_INTERVAL = 60;
const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
const REFRESH_MS = 30_000;

const DAY_KEYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// "HH:MM" -> minutes past midnight. null for anything unparseable, so a bad
// value reads as "no bound" rather than silently clamping the day shut.
const toMinutes = (hhmm) => {
  if (typeof hhmm !== "string") return null;
  const [h, m] = hhmm.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const snapUpToSlot = (d) => {
  const dt = new Date(d);
  dt.setSeconds(0, 0);
  const remainder = dt.getMinutes() % SLOT_INTERVAL;
  if (remainder !== 0) dt.setMinutes(dt.getMinutes() + (SLOT_INTERVAL - remainder));
  return dt;
};

// The earliest bookable slot: the next slot boundary at least MIN_LEAD_MINUTES away.
// Exported so the search forms pre-fill a time the grid will actually accept.
export const roundUpToSlotBoundary = (d) => {
  const target = new Date(d.getTime() + MIN_LEAD_MINUTES * 60_000);
  const dt = snapUpToSlot(target);
  // setSeconds truncated, so an exact-boundary target can land just behind it.
  if (dt < target) dt.setMinutes(dt.getMinutes() + SLOT_INTERVAL);
  return dt;
};

const pad2 = (n) => String(n).padStart(2, "0");
const toDateTimeParts = (d) => ({
  date: dayjs(d).format("YYYY-MM-DD"),
  time: `${pad2(d.getHours())}:${pad2(d.getMinutes())}`,
});

// The window a city can serve on this date, in minutes past midnight.
// undefined = hours unknown, impose nothing. null = shut that weekday.
const windowOn = (date, dayWindows) => {
  if (!dayWindows || !date) return undefined;
  const w = dayWindows[DAY_KEYS[date.getDay()]];
  if (!w) return null;
  const open = toMinutes(w.open);
  const close = toMinutes(w.close);
  return open === null || close === null ? undefined : { open, close };
};

// Can this exact moment be handed over? Past the lead-time floor AND inside the
// city's hours for its own weekday.
export const isBookableAt = (dt, dayWindows, now = new Date()) => {
  if (!dt || Number.isNaN(dt.getTime())) return false;
  if (dt < roundUpToSlotBoundary(now)) return false;
  const win = windowOn(dt, dayWindows);
  if (win === null) return false;
  if (!win) return true;
  const mins = dt.getHours() * 60 + dt.getMinutes();
  return mins >= win.open && mins <= win.close;
};

// The first slot at or after `from` that the city can actually serve, walking
// day by day past closed weekdays. Without this a Chennai visitor at 11pm is
// pre-filled with 11pm — a time no partner there opens for — and lands on an
// empty search page wondering where the bikes went.
// Returns null if nothing qualifies within maxDays.
export const firstBookableSlot = (from, dayWindows, maxDays = 8) => {
  const earliest = roundUpToSlotBoundary(from);
  if (!dayWindows) return earliest;

  for (let i = 0; i < maxDays; i++) {
    const day = new Date(earliest.getFullYear(), earliest.getMonth(), earliest.getDate() + i);
    const win = windowOn(day, dayWindows);
    if (win === null) continue;
    if (!win) return earliest;
    // Only the first day is bounded below by the lead-time floor.
    const floor = i === 0 ? earliest.getHours() * 60 + earliest.getMinutes() : 0;
    const snapped = Math.ceil(Math.max(win.open, floor) / SLOT_INTERVAL) * SLOT_INTERVAL;
    if (snapped <= win.close) {
      const at = new Date(day);
      at.setHours(Math.floor(snapped / 60), snapped % 60, 0, 0);
      return at;
    }
  }
  return null;
};

// The picker offers the earliest legal slot the moment it opens. If the customer
// then takes long enough for that slot to fall inside the lead-time floor — or the
// slot sits outside the city's opening hours at all — roll the whole booking
// forward instead of letting the API reject the search ("Start date must be 30
// minutes from now") or the search come back empty. Shifting both ends keeps the
// chosen duration, so the API's 1-hour minimum still holds.
// Returns null when the selection is still valid.
export const rollStaleStartForward = (startDateTime, endDateTime, dayWindows) => {
  if (!startDateTime?.date || !startDateTime?.time) return null;
  if (!endDateTime?.date || !endDateTime?.time) return null;
  const startDT = new Date(`${startDateTime.date}T${startDateTime.time}`);
  const endDT = new Date(`${endDateTime.date}T${endDateTime.time}`);
  if (Number.isNaN(startDT.getTime()) || Number.isNaN(endDT.getTime())) return null;

  const now = new Date();
  if (isBookableAt(startDT, dayWindows, now)) return null;

  // Search forward from their own choice, not from now, so a pickup that is
  // merely out-of-hours moves to that day's opening rather than back to today.
  const target = firstBookableSlot(startDT > now ? startDT : now, dayWindows);
  if (!target || target <= startDT) return null;

  let shiftedEnd = new Date(endDT.getTime() + (target.getTime() - startDT.getTime()));
  // The end day has its own hours, and the API checks both. Pull an overshoot back
  // to closing time, but never so far that less than an hour of rental is left.
  const endWin = windowOn(shiftedEnd, dayWindows);
  if (endWin) {
    const endMins = shiftedEnd.getHours() * 60 + shiftedEnd.getMinutes();
    if (endMins > endWin.close) {
      const capped = new Date(shiftedEnd);
      capped.setHours(Math.floor(endWin.close / 60), endWin.close % 60, 0, 0);
      if (capped.getTime() - target.getTime() >= 60 * 60_000) shiftedEnd = capped;
    }
  }

  const s = toDateTimeParts(target);
  const e = toDateTimeParts(shiftedEnd);
  return {
    startDate: s.date,
    startTime: s.time,
    endDate: e.date,
    endTime: e.time,
    startDTime: `${s.date} ${s.time}`,
    endDTime: `${e.date} ${e.time}`,
    label: dayjs(target).format("D MMM, h:mm A"),
  };
};

const formatSlotLabel = (h, m) => {
  const ampm = h >= 12 ? "PM" : "AM";
  const hh = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${String(hh).padStart(2, "0")}:${String(m).padStart(2, "0")} ${ampm}`;
};

const format24 = (h, m) => `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;

const ALL_SLOTS = Array.from({ length: (24 * 60) / SLOT_INTERVAL }, (_, i) => {
  const totalMinutes = i * SLOT_INTERVAL;
  const hour = Math.floor(totalMinutes / 60);
  const minute = totalMinutes % 60;
  return { index: i, hour, minute, label: formatSlotLabel(hour, minute), value: format24(hour, minute) };
});

const DateTimeCard = ({
  label = "Select Date & Time",
  selectedDate,
  setSelectedDate,
  selectedTime,
  setSelectedTime,
  setIsPickerOpen,
  minDate,
  maxDate,
  type,
  setSelectedIsoDate,
  dayWindows,
  mobile = false,
}) => {
  const dispatch = useDispatch();
  const searchSelector = useSelector((state) => state.search);

  const [step, setStep] = useState("date");

  const [currentMonth, setCurrentMonth] = useState(() => {
    const init = selectedDate ? new Date(selectedDate) : new Date();
    return new Date(init.getFullYear(), init.getMonth(), 1);
  });

  const [pickedDate, setPickedDate] = useState(() => {
    if (selectedDate) return new Date(selectedDate);
    const rounded = roundUpToSlotBoundary(new Date());
    return new Date(rounded.getFullYear(), rounded.getMonth(), rounded.getDate());
  });

  // Match the whole time, not just the hour. Under a 30-minute SLOT_INTERVAL an
  // "17:30" would otherwise highlight the 17:00 chip, and handleDateClick would
  // carry that wrong time forward. No match highlights nothing, never a guess.
  const [pickedSlotIdx, setPickedSlotIdx] = useState(() => {
    if (!selectedTime) return -1;
    const [h, m] = selectedTime.split(":").map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return -1;
    return ALL_SLOTS.findIndex((s) => s.hour === h && s.minute === m);
  });

  const [feedback, setFeedback] = useState(null);
  const [nowTime, setNowTime] = useState(new Date());

  useEffect(() => {
    const id = setInterval(() => setNowTime(new Date()), REFRESH_MS);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") setIsPickerOpen(false); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [setIsPickerOpen]);

  const isCurrentMonth = useMemo(() => {
    const n = new Date();
    return currentMonth.getFullYear() === n.getFullYear() && currentMonth.getMonth() === n.getMonth();
  }, [currentMonth]);

  const canGoPrev = useMemo(() => {
    const n = new Date();
    return currentMonth.getFullYear() > n.getFullYear() ||
      (currentMonth.getFullYear() === n.getFullYear() && currentMonth.getMonth() > n.getMonth());
  }, [currentMonth]);

  const canGoNext = useMemo(() => {
    if (!maxDate) return true;
    const max = new Date(maxDate);
    return currentMonth.getFullYear() < max.getFullYear() ||
      (currentMonth.getFullYear() === max.getFullYear() && currentMonth.getMonth() < max.getMonth());
  }, [currentMonth, maxDate]);

  const calendarWeeks = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    let startWd = (firstDay.getDay() + 6) % 7;
    const daysInMonth = lastDay.getDate();
    const totalCells = Math.ceil((startWd + daysInMonth) / 7) * 7;
    const cells = [];
    for (let i = 0; i < totalCells; i++) {
      const dayNum = i - startWd + 1;
      if (dayNum < 1 || dayNum > daysInMonth) {
        cells.push(null);
      } else {
        cells.push(new Date(year, month, dayNum));
      }
    }
    const weeks = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
    return weeks;
  }, [currentMonth]);

  // Both bounds are fixed for a given render, so derive them once instead of
  // rebuilding them inside the per-cell, per-slot loops below.
  const earliestSlotDT = useMemo(() => roundUpToSlotBoundary(nowTime), [nowTime]);
  const minDateDT = useMemo(() => (minDate ? new Date(minDate) : null), [minDate]);

  const windowFor = useCallback((date) => windowOn(date, dayWindows), [dayWindows]);

  // Compare whole datetimes, not minutes-of-day: at 23:45 the earliest slot is
  // 01:00 *tomorrow*, so a minutes-only check would wrongly offer tomorrow 00:00.
  // `now` defaults to the 30s-ticking clock used for rendering; commit paths pass a
  // fresh Date so a slot that expired since the last tick can't slip through.
  const isSlotSelectableOn = useCallback(
    (date, slot, now) => {
      if (!date) return false;
      const win = windowFor(date);
      if (win === null) return false;
      if (win) {
        const mins = slot.hour * 60 + slot.minute;
        // Inclusive of close, matching Core::checkTimings on the API side.
        if (mins < win.open || mins > win.close) return false;
      }
      const slotDT = new Date(date.getFullYear(), date.getMonth(), date.getDate(), slot.hour, slot.minute, 0, 0);
      if (slotDT < (now ? roundUpToSlotBoundary(now) : earliestSlotDT)) return false;
      if (minDateDT && slotDT < minDateDT) return false;
      return true;
    },
    [minDateDT, earliestSlotDT, windowFor]
  );

  // The time bounds are floors and slots ascend through the day, so whichever slot
  // sits latest inside the opening window is the best candidate: if it fails, all
  // earlier ones fail too. Keeps this O(1) per calendar cell rather than O(24).
  const hasAnySlot = useCallback(
    (date) => {
      const win = windowFor(date);
      if (win === null) return false;
      const last = win
        ? [...ALL_SLOTS].reverse().find((s) => s.hour * 60 + s.minute <= win.close)
        : ALL_SLOTS[ALL_SLOTS.length - 1];
      return last ? isSlotSelectableOn(date, last) : false;
    },
    [isSlotSelectableOn, windowFor]
  );

  const isDateSelectable = useCallback(
    (d) => {
      if (!d) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const check = new Date(d);
      check.setHours(0, 0, 0, 0);
      if (check < today) return false;
      if (minDate) {
        const min = new Date(minDate);
        min.setHours(0, 0, 0, 0);
        if (check < min) return false;
      }
      if (maxDate) {
        const max = new Date(maxDate);
        max.setHours(0, 0, 0, 0);
        if (check > max) return false;
      }
      return hasAnySlot(d);
    },
    [minDate, maxDate, hasAnySlot]
  );

  // Writes the selection through to redux + the parent field. Returns an error
  // string when the pair is not bookable, null on success. Every path that can
  // change the stored value goes through here so what the grid highlights is
  // always what is actually stored.
  const commitSelection = useCallback(
    (dateObj, slot, { close }) => {
      if (!dateObj || !slot) return "Select a date and time";
      if (!isSlotSelectableOn(dateObj, slot, new Date())) return "Selected time is no longer available";

      const dateStr = dayjs(dateObj).format("YYYY-MM-DD");
      const timeStr = slot.value;

      if (type === "endDate") {
        const startDT = searchSelector?.startDateTime;
        if (startDT?.date && startDT?.time) {
          if (new Date(`${dateStr}T${timeStr}`) <= new Date(`${startDT.date}T${startDT.time}`)) {
            return "Drop off must be after pickup";
          }
        }
      }

      if (type === "startDate") {
        dispatch(getStartDate({ date: dateStr, time: timeStr }));
      } else if (type === "endDate") {
        dispatch(getEndDate({ date: dateStr, time: timeStr }));
      }

      setPickedDate(dateObj);
      setPickedSlotIdx(slot.index);
      setSelectedDate(dateStr);
      setSelectedTime(timeStr);
      if (setSelectedIsoDate) setSelectedIsoDate(dateStr);
      if (close) setIsPickerOpen(false);
      return null;
    },
    [isSlotSelectableOn, type, searchSelector?.startDateTime, dispatch, setSelectedDate, setSelectedTime, setSelectedIsoDate, setIsPickerOpen]
  );

  const handleDateClick = (d) => {
    if (!isDateSelectable(d)) return;
    setPickedDate(d);
    setCurrentMonth(new Date(d.getFullYear(), d.getMonth(), 1));
    setFeedback(null);
    setStep("time");
    // Carry the existing time onto the new date so the highlighted slot is one
    // that is really stored; closing here would otherwise keep the old date.
    const carried = pickedSlotIdx >= 0 ? ALL_SLOTS[pickedSlotIdx] : null;
    if (carried && commitSelection(d, carried, { close: false })) setPickedSlotIdx(-1);
  };

  const handlePrevMonth = () => {
    setCurrentMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    if (!canGoNext) return;
    setCurrentMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const handleMonthLabelClick = () => {
    const today = new Date();
    setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));
  };

  const handleSlotClick = (slot) => {
    const err = commitSelection(pickedDate, slot, { close: true });
    if (err) setFeedback(err);
  };

  const touchStartX = useRef(null);
  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0 && canGoPrev) handlePrevMonth();
      else if (diff < 0 && canGoNext) handleNextMonth();
    }
    touchStartX.current = null;
  };

  const cityName = searchSelector?.city?.name;

  // Explains a trimmed time grid. Only shown when the hours are genuinely
  // restricted — a 24-hour city says nothing rather than stating the obvious.
  const hoursNote = useMemo(() => {
    const win = windowFor(pickedDate);
    if (!win) return null;
    if (win.open <= 0 && win.close >= 23 * 60) return null;
    const at = (mins) => {
      const d = new Date(2000, 0, 1, Math.floor(mins / 60), mins % 60);
      return dayjs(d).format("h:mm A");
    };
    const where = cityName ? `Partners in ${cityName}` : "Partners";
    return `${where} operate ${at(win.open)} – ${at(win.close)}`;
  }, [windowFor, pickedDate, cityName]);

  const isSelected = (d) => pickedDate && isSameDay(d, pickedDate);
  const isToday = (d) => isSameDay(d, new Date());

  return (
      <div className={`${mobile ? "relative top-0 left-0 w-full shadow-[0_8px_24px_rgba(0,0,0,0.1),0_0_0_1px_rgba(0,0,0,0.04)]" : "absolute top-[calc(100%+8px)] left-0 w-[340px] max-[844px]:w-[min(340px,calc(100vw-16px))]"} bg-white rounded-2xl flex flex-col shadow-[0_12px_40px_rgba(0,0,0,0.12),0_0_0_1px_rgba(0,0,0,0.04)] animate-[dtcDropdownFadeIn_0.2s_ease-out] origin-top overflow-hidden z-50`} onClick={(e) => e.stopPropagation()} role="dialog" aria-label={label}>
        <div className="relative flex items-center justify-center h-11 px-1">
          {step === "time" && (
            <button className="absolute left-0 top-0 w-11 h-11 flex items-center justify-center border-none bg-transparent cursor-pointer rounded-full text-[#757575] transition-colors touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 [@media(hover:hover)]:hover:bg-gray-100 [&_svg]:w-4 [&_svg]:h-4" onClick={() => { setStep("date"); setFeedback(null); }} aria-label="Back to date">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
          )}
          <span className="text-[17px] font-semibold text-textPrimary text-center">{label}</span>
          <button className="absolute right-0 top-0 w-11 h-11 flex items-center justify-center border-none bg-transparent cursor-pointer rounded-full text-[#757575] transition-colors touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 [@media(hover:hover)]:hover:bg-gray-100 [&_svg]:w-4 [&_svg]:h-4" onClick={() => setIsPickerOpen(false)} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {feedback && (
          <div role="alert" className="flex items-center gap-1 py-1.5 px-2 mx-2 mt-1.5 bg-amber-50 rounded-lg text-[14px] text-orange-800 [&_svg]:w-3.5 [&_svg]:h-3.5 [&_svg]:text-orange-400 [&_svg]:shrink-0">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/></svg>
            {feedback}
          </div>
        )}

        {hoursNote && (
          <p className="flex items-center justify-center gap-1.5 mx-2 mt-1.5 mb-0 py-1.5 px-2 rounded-lg bg-redSurface-50 text-[13px] font-medium text-primary-deep [&_svg]:w-3.5 [&_svg]:h-3.5 [&_svg]:shrink-0">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 14"/></svg>
            {hoursNote}
          </p>
        )}

        {step === "date" ? (
          <div key="date" className="px-2 pb-2 flex flex-col select-none animate-[dtcDropdownFadeIn_0.15s_ease-out]" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
            <div className="flex items-center h-11">
              <button className="w-11 h-11 flex items-center justify-center border-none bg-transparent cursor-pointer rounded-full transition-colors shrink-0 touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 [@media(hover:hover)]:hover:enabled:bg-gray-100 disabled:cursor-default disabled:opacity-35 [&_svg]:w-3.5 [&_svg]:h-3.5" onClick={handlePrevMonth} disabled={!canGoPrev} aria-label="Previous month">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
              </button>
              <button type="button" className="flex-1 h-11 px-1 text-center cursor-pointer bg-transparent border-none touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-lg" onClick={handleMonthLabelClick} aria-label={`${dayjs(currentMonth).format("MMMM YYYY")} — jump to current month`}>
                <div className="text-[16px] font-semibold text-textPrimary whitespace-nowrap overflow-hidden text-ellipsis">
                  {dayjs(currentMonth).format("MMMM YYYY")}
                </div>
                {!isCurrentMonth && <div className="text-[12px] text-primary font-medium">Back to today</div>}
              </button>
              <button className="w-11 h-11 flex items-center justify-center border-none bg-transparent cursor-pointer rounded-full transition-colors shrink-0 touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 [@media(hover:hover)]:hover:enabled:bg-gray-100 disabled:cursor-default disabled:opacity-35 [&_svg]:w-3.5 [&_svg]:h-3.5" onClick={handleNextMonth} disabled={!canGoNext} aria-label="Next month">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
            </div>

            <div className="grid grid-cols-7 text-center pb-0.5 border-b border-gray-200">
              {WEEKDAYS.map((wd) => (
                <span key={wd} className="text-[12px] font-medium text-gray-500 uppercase tracking-wide">{wd}</span>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-y-1 mt-0.5">
              {calendarWeeks.flat().map((d, i) => {
                const selectable = d ? isDateSelectable(d) : false;
                return (
                  <div key={i} className="flex items-center justify-center">
                    {d ? (
                      <button
                        className={`w-11 h-11 flex items-center justify-center border-none bg-transparent cursor-pointer rounded-full text-[16px] font-normal text-textPrimary transition-colors touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${isSelected(d) && selectable ? "!bg-primary !text-white !font-semibold" : ""} ${isToday(d) && !(isSelected(d) && selectable) ? "border-[1.5px] !border-primary !text-primary !font-semibold" : ""} ${!selectable ? "!text-textMutedSoft !cursor-default !pointer-events-none" : "[@media(hover:hover)]:hover:bg-gray-50"}`}
                        onClick={() => handleDateClick(d)}
                        disabled={!selectable}
                        aria-label={dayjs(d).format("D MMMM YYYY")}
                        aria-pressed={(isSelected(d) && selectable) || undefined}
                      >
                        {d.getDate()}
                      </button>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div key="time" className="px-2 pb-2 flex flex-col animate-[dtcDropdownFadeIn_0.15s_ease-out]">
            <button
              className="self-center inline-flex items-center gap-1.5 min-h-[44px] px-4 my-1.5 bg-gray-50 border border-gray-200 rounded-full text-[14px] font-semibold text-textPrimary cursor-pointer transition-colors touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 [@media(hover:hover)]:hover:bg-gray-100 [&_svg]:w-3 [&_svg]:h-3 [&_svg]:text-gray-500 [&_svg]:shrink-0"
              onClick={() => { setStep("date"); setFeedback(null); }}
              aria-label="Change date"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              {dayjs(pickedDate).format("ddd, D MMM YY")}
            </button>

            {!hasAnySlot(pickedDate) && (
              <div className="flex items-center gap-1 py-1 px-2 mb-1.5 bg-amber-50 rounded-lg text-[14px] text-orange-800 [&_svg]:w-3.5 [&_svg]:h-3.5 [&_svg]:text-orange-400 [&_svg]:shrink-0">
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/></svg>
                No time left on this date — pick another day
              </div>
            )}

            <div className="grid grid-cols-4 gap-2">
              {ALL_SLOTS.map((slot) => {
                const selectable = isSlotSelectableOn(pickedDate, slot);
                return (
                  <button
                    key={slot.index}
                    aria-pressed={pickedSlotIdx === slot.index && selectable ? true : undefined}
                    className={`flex items-center justify-center h-11 px-0.5 rounded-lg border border-gray-200 text-[14px] font-normal text-textPrimary cursor-pointer transition-colors whitespace-nowrap touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${pickedSlotIdx === slot.index && selectable ? "!bg-primary !text-white !font-semibold !border-primary" : ""} ${!selectable ? "!text-textMutedSoft !border-gray-100 !cursor-default !pointer-events-none" : "[@media(hover:hover)]:hover:bg-gray-50"}`}
                    onClick={() => handleSlotClick(slot)}
                    disabled={!selectable}
                  >
                    {slot.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

      </div>
  );
};

export default DateTimeCard;
